import { Link } from 'react-router-dom';
import {
  UXP_FI_ID,
  COMMAND_CENTER_METRICS,
  SETTLEMENT_FEATURES,
  FEE_TYPES,
  PROFITABILITY_LEVELS,
  COST_CATEGORIES,
  CASH_FLOW_METRICS,
  GST_FEATURES,
  ACCOUNTING_FEATURES,
  RECONCILIATION_TYPES,
  CLAIM_TYPES,
  FORECAST_METRICS,
  CFO_CAPABILITIES,
  SCENARIO_TYPES,
  REPORT_TYPES,
  AUDIT_FEATURES,
  ANALYTICS_METRICS,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_KPIS,
  DEMO_SETTLEMENTS,
  DEMO_PROFITABILITY,
  DEMO_FEES,
  DEMO_CASH_FLOW,
  DEMO_GST,
  DEMO_RECONCILIATIONS,
  DEMO_CFO,
  buildFinancialIntelDashboard,
} from '../../financialintel/framework';
import {
  ExecutiveFinancialCommandCenterPanel,
  SettlementIntelligencePanel,
  ProfitabilityEnginePanel,
  FeeIntelligencePanel,
  CashFlowIntelligencePanel,
  GSTIntelligencePanel,
  ReconciliationPanel,
  AICFOAdvisorPanel,
} from '../../components/financialintel';
import { Card, KpiCard } from '../../components/ui';

export function FinancialIntelligencePage() {
  const dashboard = buildFinancialIntelDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_FI_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Financial Intelligence Platform</h1>
        <p className="mt-1 text-sm text-slate-500">
          Financial Intelligence, Settlement, Profitability, GST & Accounting Operating System —
          unifies settlements, accounting, GST, profitability, cash flow, and AI-driven financial guidance.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-medium text-orange-700 hover:bg-orange-100 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Command Center Metrics" value={dashboard.command_center_metrics.toString()} />
        <KpiCard label="Settlement Features" value={dashboard.settlement_features.toString()} />
        <KpiCard label="Profitability Levels" value={dashboard.profitability_levels.toString()} />
        <KpiCard label="GST Features" value={dashboard.gst_features.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Executive Financial Command Center</h2>
        <p className="mt-1 text-sm text-slate-500">{COMMAND_CENTER_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ExecutiveFinancialCommandCenterPanel kpis={DEMO_KPIS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Settlement Intelligence</h2>
        <p className="mt-1 text-sm text-slate-500">{SETTLEMENT_FEATURES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <SettlementIntelligencePanel settlements={DEMO_SETTLEMENTS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Profitability Engine</h2>
          <p className="mt-1 text-sm text-slate-500">{PROFITABILITY_LEVELS.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <ProfitabilityEnginePanel rows={DEMO_PROFITABILITY} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Fee Intelligence</h2>
          <p className="mt-1 text-sm text-slate-500">{FEE_TYPES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <FeeIntelligencePanel fees={DEMO_FEES} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Cost Intelligence</h2>
        <p className="mt-1 text-sm text-slate-500">{COST_CATEGORIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <div className="flex flex-wrap gap-2">
            {COST_CATEGORIES.map((c) => (
              <span key={c} className="rounded border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{c}</span>
            ))}
          </div>
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Cash Flow Intelligence</h2>
        <p className="mt-1 text-sm text-slate-500">{CASH_FLOW_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <CashFlowIntelligencePanel rows={DEMO_CASH_FLOW} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">GST Intelligence</h2>
          <p className="mt-1 text-sm text-slate-500">{GST_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <GSTIntelligencePanel returns={DEMO_GST} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Accounting Integration</h2>
          <p className="mt-1 text-sm text-slate-500">{ACCOUNTING_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <div className="space-y-2 text-sm">
              <p className="font-medium">TallyPrime connected · 847 vouchers queued</p>
              <p className="text-slate-600 dark:text-slate-400">Last sync: Aug 1, 06:00 IST · 0 validation errors</p>
              <p className="text-xs text-brand-600">Ledger mapping: Amazon Settlement → Bank · GST → Output Tax</p>
            </div>
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Reconciliation</h2>
        <p className="mt-1 text-sm text-slate-500">{RECONCILIATION_TYPES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ReconciliationPanel rows={DEMO_RECONCILIATIONS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Claims Management</h2>
        <p className="mt-1 text-sm text-slate-500">{CLAIM_TYPES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <div className="space-y-2 text-sm">
            <p><span className="font-medium">CLM-2026-0184</span> — Lost inventory · ₹42K · Submitted · Recovery pending</p>
            <p><span className="font-medium">CLM-2026-0183</span> — Incorrect FBA fee · ₹8.4K · Approved · Credited</p>
            <p className="text-xs text-slate-500">AI recommendation: 3 additional fee discrepancy claims worth ₹18K</p>
          </div>
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI CFO</h2>
        <p className="mt-1 text-sm text-slate-500">{CFO_CAPABILITIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AICFOAdvisorPanel recommendations={DEMO_CFO} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Scenario Planning</h2>
          <p className="mt-1 text-sm text-slate-500">{SCENARIO_TYPES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <div className="space-y-2 text-sm">
              <p className="font-medium">Active: +10% ad spend scenario</p>
              <p className="text-slate-600 dark:text-slate-400">Projected: +₹2.4L net profit · -₹8L working capital impact</p>
            </div>
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Reporting</h2>
          <p className="mt-1 text-sm text-slate-500">{REPORT_TYPES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <div className="flex flex-wrap gap-2">
              {REPORT_TYPES.slice(0, 4).map((r) => (
                <span key={r} className="rounded border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{r}</span>
              ))}
            </div>
          </Card>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Forecasting</h2>
          <p className="mt-2 text-xs text-slate-500">{FORECAST_METRICS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Audit & Governance</h2>
          <p className="mt-2 text-xs text-slate-500">{AUDIT_FEATURES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Analytics</h2>
          <p className="mt-2 text-xs text-slate-500">{ANALYTICS_METRICS.join(' · ')}</p>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Accessibility</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ACCESSIBILITY_STANDARDS.map((s) => (
            <span key={s} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{s}</span>
          ))}
        </div>
      </section>
    </div>
  );
}
